import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Check, ChevronDown, ChevronUp, Building2, Sparkles, CheckCircle2 } from "lucide-react";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

const sanitizeHtml = (html) => DOMPurify.sanitize(html || "", {
  ALLOWED_TAGS: ["p","br","strong","em","u","s","ul","ol","li","a","h1","h2","h3","h4","h5","h6","blockquote","code","pre","span","div"],
  ALLOWED_ATTR: ["href","target","rel","class","style"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
});

const CYCLE_LABEL = { mensile: "/mese", semestrale: "/semestre", annuale: "/anno", una_tantum: " una tantum" };

export default function Landing() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [billingCycle, setBillingCycle] = useState("mensile"); // "mensile" | "annuale"
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [heroFailed, setHeroFailed] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  // 🆕 Feb 2026 — Richiesta demo dalla landing
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: "", surname: "", email: "", phone: "", agency_name: "", vat_number: "", fiscal_code: "", website: "" });
  // 🆕 Feb 2026 — Anti-spam: tempo di apertura dialog + honeypot invisibile
  const [demoOpenedAt, setDemoOpenedAt] = useState(0);

  // Validatori lato client (allineati al backend)
  const isValidPhone = (raw) => {
    const s = String(raw || "").replace(/[\s\-().]/g, "").replace(/^00/, "+");
    const digits = s.startsWith("+") ? s.slice(1) : s;
    return /^\d{8,15}$/.test(digits);
  };
  const isValidVat = (raw) => {
    const s = String(raw || "").replace(/\s/g, "").toUpperCase().replace(/^IT/, "");
    return /^\d{11}$/.test(s);
  };
  const isValidFiscalCode = (raw) => {
    const s = String(raw || "").replace(/\s/g, "").toUpperCase();
    return /^[A-Z0-9]{16}$/.test(s) || /^\d{11}$/.test(s);
  };

  const submitDemoRequest = async () => {
    const f = demoForm;
    if (!f.name.trim() || !f.surname.trim() || !f.email.trim() || !f.phone.trim() || !f.agency_name.trim() || !f.vat_number.trim() || !f.fiscal_code.trim()) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (!isValidPhone(f.phone)) {
      toast.error("Numero di telefono non valido (min 8, max 15 cifre)");
      return;
    }
    if (!isValidVat(f.vat_number)) {
      toast.error("Partita IVA non valida (11 cifre)");
      return;
    }
    if (!isValidFiscalCode(f.fiscal_code)) {
      toast.error("Codice Fiscale non valido (16 alfanumerici oppure 11 cifre)");
      return;
    }
    // 🆕 Anti-spam: se il form viene inviato < 2 secondi dall'apertura, silent-drop (probabile bot)
    if (demoOpenedAt && Date.now() - demoOpenedAt < 2000) {
      // Simula il successo per non dare feedback ai bot
      setDemoSubmitted(true);
      return;
    }
    setDemoSubmitting(true);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      await axios.post(`${apiBase}/public/demo-requests`, f);
      // 🆕 Non chiudere il dialog: mostra messaggio fisso configurato dal super admin
      setDemoSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setDemoSubmitting(false);
    }
  };

  const closeDemoDialog = () => {
    setDemoOpen(false);
    // Reset stato dopo animazione chiusura
    setTimeout(() => {
      setDemoSubmitted(false);
      setDemoForm({ name: "", surname: "", email: "", phone: "", agency_name: "", vat_number: "", fiscal_code: "", website: "" });
    }, 200);
  };

  useEffect(() => {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    axios.get(`${apiBase}/public/landing`)
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  // 🆕 Numero immagini disponibili (safe con data null)
  const heroImagesCount = (() => {
    if (!data?.settings) return 0;
    let n = 0;
    if (data.settings.hero_image_url) n++;
    n += (data.settings.gallery || []).filter((g) => g.url).length;
    return n;
  })();

  // 🆕 Rotazione automatica ogni 5 sec (2+ immagini)
  useEffect(() => {
    if (heroImagesCount < 2) return;
    const t = setInterval(() => setHeroIndex((i) => (i + 1) % heroImagesCount), 5000);
    return () => clearInterval(t);
  }, [heroImagesCount]);

  // 🆕 Reset stato caricamento al cambio immagine
  useEffect(() => { setHeroLoaded(false); }, [heroIndex]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="landing-loading">
      <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…
    </div>;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center px-4" data-testid="landing-disabled">
        <Building2 className="h-12 w-12 text-slate-300 mb-3" />
        <h1 className="font-display text-2xl font-semibold text-slate-700">Pagina non disponibile</h1>
        <p className="text-slate-500 mt-2 max-w-md">La landing pubblica non è al momento attiva. Se sei un&apos;agenzia già registrata, puoi accedere dal pannello.</p>
        <Button onClick={() => nav("/login")} className="mt-6 bg-[#0A192F] hover:bg-[#1E293B]">Vai al login</Button>
      </div>
    );
  }

  const { settings, plans } = data;
  const ordered = [...(plans || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const hasAnyAnnual = ordered.some((p) => p.annual_price && Number(p.annual_price) > 0);
  // 🆕 30/07/2026 — Se almeno un piano ha `semestral_price`, aggiungi "Semestrale" al toggle
  const hasAnySemestral = ordered.some((p) => p.semestral_price && Number(p.semestral_price) > 0);
  const hasAnyMultiCycle = hasAnyAnnual || hasAnySemestral;

  // Costruisce elenco immagini per la hero (immagine principale + galleria)
  const heroImages = (() => {
    const arr = [];
    if (settings.hero_image_url) arr.push({ id: "hero", url: settings.hero_image_url });
    (settings.gallery || []).forEach((g) => { if (g.url) arr.push({ id: g.id, url: g.url }); });
    return arr;
  })();

  const currentHero = heroImages[heroIndex % (heroImages.length || 1)] || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="landing-page">
      {/* Top bar */}
      <div className="border-b border-slate-200/70 bg-white/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo size="lg" testId="landing-brand-logo" />
          </div>
          <Button onClick={() => nav("/login")} variant="outline" size="sm" data-testid="landing-login-btn">
            Accedi
          </Button>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-16 pb-12">
        <div className={`grid gap-10 items-center ${currentHero && !heroFailed ? "lg:grid-cols-2" : ""}`}>
          <div className={currentHero && !heroFailed ? "text-left" : "text-center max-w-3xl mx-auto"}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
              {settings.hero_title}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mt-5 leading-relaxed">
              {settings.hero_subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="landing-hero-cta"
                onClick={() => {
                  document.getElementById("plans")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="bg-[#0A192F] hover:bg-[#1E293B] text-white px-8 py-6 text-base rounded-full"
              >
                {settings.hero_cta || "Inizia ora"} →
              </Button>
              <Button
                data-testid="landing-demo-request-btn"
                onClick={() => { setDemoOpen(true); setDemoOpenedAt(Date.now()); }}
                variant="outline"
                className="border-2 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white px-8 py-6 text-base rounded-full"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Richiedi una demo
              </Button>
            </div>
          </div>
          {currentHero && !heroFailed && (
            <div className="order-first lg:order-last">
              <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-200/60 bg-slate-100">
                {/* Skeleton shimmer mostrato fino al caricamento */}
                {!heroLoaded && (
                  <div
                    data-testid="landing-hero-skeleton"
                    className="absolute inset-0 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 animate-pulse"
                    style={{ backgroundSize: "200% 100%", animation: "shimmer 1.6s ease-in-out infinite" }}
                  />
                )}
                <img
                  key={currentHero.id}
                  src={currentHero.url}
                  alt="Anteprima piattaforma"
                  data-testid="landing-hero-image"
                  onLoad={() => setHeroLoaded(true)}
                  onError={() => setHeroFailed(true)}
                  className={`relative w-full h-full object-cover transition-opacity duration-500 ${heroLoaded ? "opacity-100" : "opacity-0"}`}
                />
                {/* 🆕 Indicatori dei punti (solo se ci sono 2+ immagini) */}
                {heroImages.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 bg-black/40 backdrop-blur px-2 py-1 rounded-full" data-testid="hero-carousel-dots">
                    {heroImages.map((h, i) => (
                      <button
                        key={h.id}
                        type="button"
                        onClick={() => setHeroIndex(i)}
                        aria-label={`Vai a immagine ${i + 1}`}
                        className={`w-2 h-2 rounded-full transition-all ${i === (heroIndex % heroImages.length) ? "bg-white scale-125" : "bg-white/50 hover:bg-white/80"}`}
                        data-testid={`hero-dot-${i}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Plans */}
      {ordered.length > 0 && (
        <section id="plans" className="max-w-6xl mx-auto px-5 py-12">
          <div className="text-center mb-6">
            <h2 className="font-display text-3xl font-semibold text-slate-900">Scegli il piano adatto a te</h2>
            <p className="text-sm text-slate-500 mt-2">Tutti i piani includono assistenza email. Puoi cambiare in qualsiasi momento.</p>
          </div>
          {hasAnyMultiCycle && (
            <div className="flex justify-center mb-8" data-testid="billing-cycle-toggle">
              <div className="inline-flex bg-white border border-slate-200 rounded-full p-1 shadow-sm">
                <button
                  type="button"
                  data-testid="cycle-mensile"
                  onClick={() => setBillingCycle("mensile")}
                  className={`px-5 py-2 text-sm font-medium rounded-full transition ${billingCycle === "mensile" ? "bg-[#0A192F] text-white shadow" : "text-slate-600 hover:text-slate-900"}`}
                >
                  Mensile
                </button>
                {hasAnySemestral && (
                  <button
                    type="button"
                    data-testid="cycle-semestrale"
                    onClick={() => setBillingCycle("semestrale")}
                    className={`px-5 py-2 text-sm font-medium rounded-full transition flex items-center gap-2 ${billingCycle === "semestrale" ? "bg-[#0A192F] text-white shadow" : "text-slate-600 hover:text-slate-900"}`}
                  >
                    Semestrale
                    <span className="text-[10px] font-semibold bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded-full">RISPARMI</span>
                  </button>
                )}
                {hasAnyAnnual && (
                  <button
                    type="button"
                    data-testid="cycle-annuale"
                    onClick={() => setBillingCycle("annuale")}
                    className={`px-5 py-2 text-sm font-medium rounded-full transition flex items-center gap-2 ${billingCycle === "annuale" ? "bg-[#0A192F] text-white shadow" : "text-slate-600 hover:text-slate-900"}`}
                  >
                    Annuale
                    <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">RISPARMI</span>
                  </button>
                )}
              </div>
            </div>
          )}
          <div className={`grid gap-6 ${ordered.length === 1 ? "max-w-md mx-auto" : ordered.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-2 lg:grid-cols-3"}`}>
            {ordered.map((p) => {
              const hasAnnual = p.annual_price && Number(p.annual_price) > 0;
              const hasSemestral = p.semestral_price && Number(p.semestral_price) > 0;
              // displayPrice e suffix dipendono dal toggle
              let displayPrice = Number(p.price);
              let originalPrice = null; // prezzo barrato se c'è uno sconto attivo
              let suffix = CYCLE_LABEL[p.billing_cycle] || "";
              let saveBadge = null;
              if (hasAnyMultiCycle) {
                if (billingCycle === "annuale" && hasAnnual) {
                  const basePrice = Number(p.annual_price);
                  const discountPct = Number(p.annual_discount_percent) || 0;
                  if (discountPct > 0 && discountPct < 100) {
                    displayPrice = Math.round((basePrice * (1 - discountPct / 100)) * 100) / 100;
                    originalPrice = basePrice;
                    saveBadge = `-${Math.round(discountPct)}%`;
                  } else {
                    displayPrice = basePrice;
                    if (p.price > 0) {
                      const monthlyYearly = Number(p.price) * 12;
                      if (monthlyYearly > basePrice) {
                        const pct = Math.round((1 - basePrice / monthlyYearly) * 100);
                        if (pct > 0) { saveBadge = `-${pct}%`; originalPrice = monthlyYearly; }
                      }
                    }
                  }
                  suffix = "/anno";
                } else if (billingCycle === "semestrale" && hasSemestral) {
                  // 🆕 30/07/2026 — Prezzo semestrale con eventuale sconto
                  const basePrice = Number(p.semestral_price);
                  const discountPct = Number(p.semestral_discount_percent) || 0;
                  if (discountPct > 0 && discountPct < 100) {
                    displayPrice = Math.round((basePrice * (1 - discountPct / 100)) * 100) / 100;
                    originalPrice = basePrice;
                    saveBadge = `-${Math.round(discountPct)}%`;
                  } else {
                    displayPrice = basePrice;
                    if (p.price > 0) {
                      const monthlySemestral = Number(p.price) * 6;
                      if (monthlySemestral > basePrice) {
                        const pct = Math.round((1 - basePrice / monthlySemestral) * 100);
                        if (pct > 0) { saveBadge = `-${pct}%`; originalPrice = monthlySemestral; }
                      }
                    }
                  }
                  suffix = "/semestre";
                } else {
                  // Mensile o piano senza il ciclo selezionato → fallback a mensile
                  displayPrice = Number(p.price);
                  suffix = "/mese";
                }
              }
              const ctaUrl = `/signup?plan=${p.id}${hasAnyMultiCycle ? `&cycle=${billingCycle}` : ""}`;
              return (
                <Card key={p.id} data-testid={`plan-card-${p.id}`} className={`relative ${p.badge ? "border-blue-500 shadow-lg ring-2 ring-blue-200" : "border-slate-200"}`}>
                  {p.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[11px] font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                      {p.badge}
                    </div>
                  )}
                  <CardContent className="pt-8 pb-6 px-6 flex flex-col h-full">
                    <h3 className="font-display text-xl font-semibold text-slate-900">{p.name}</h3>
                    <div className="mt-3 mb-2 flex items-baseline gap-2 flex-wrap">
                      {originalPrice !== null && (billingCycle === "annuale" || billingCycle === "semestrale") && (
                        <span className="text-lg text-slate-400 line-through" data-testid={`plan-original-price-${p.id}`}>€{originalPrice.toFixed(0)}</span>
                      )}
                      <span className="text-4xl font-bold text-slate-900" data-testid={`plan-price-${p.id}`}>€{displayPrice.toFixed(0)}</span>
                      <span className="text-sm text-slate-500">{suffix}</span>
                      {saveBadge && (billingCycle === "annuale" || billingCycle === "semestrale") && (
                        <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full" data-testid={`plan-save-badge-${p.id}`}>{saveBadge}</span>
                      )}
                    </div>
                    {hasAnyMultiCycle && billingCycle === "annuale" && hasAnnual && displayPrice > 0 && (
                      <p className="text-[11px] text-slate-400 mb-3">
                        Equivalente a {formatCurrency(displayPrice / 12)}/mese
                      </p>
                    )}
                    {hasAnyMultiCycle && billingCycle === "semestrale" && hasSemestral && displayPrice > 0 && (
                      <p className="text-[11px] text-slate-400 mb-3">
                        Equivalente a {formatCurrency(displayPrice / 6)}/mese
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mb-4">Abbonamento di <strong>{p.duration_days} giorni</strong></p>
                    <ul className="space-y-2 mb-6 flex-1">
                      {(p.features || []).map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      data-testid={`plan-cta-${p.id}`}
                      onClick={() => nav(ctaUrl)}
                      className={`w-full ${p.badge ? "bg-blue-600 hover:bg-blue-700" : "bg-[#0A192F] hover:bg-[#1E293B]"} text-white`}
                    >
                      Scegli questo piano
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Come funziona */}
      {(settings.how_it_works || []).length > 0 && (
        <section className="bg-white border-y border-slate-200/70 py-16">
          <div className="max-w-5xl mx-auto px-5">
            <h2 className="font-display text-3xl font-semibold text-center text-slate-900 mb-3">{settings.how_it_works_title || "Come funziona"}</h2>
            {settings.how_it_works_subtitle && (
              <p className="text-center text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">{settings.how_it_works_subtitle}</p>
            )}
            <div className="space-y-4">
              {settings.how_it_works.map((s, i) => (
                <div key={i} data-testid={`hiw-step-${i}`} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 flex items-start gap-5">
                  <div className="w-14 h-14 shrink-0 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold flex items-center justify-center text-xl">
                    {s.icon ? <span className="text-2xl" aria-hidden>{s.icon}</span> : (i + 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 text-lg mb-2">
                      {s.title}
                    </h3>
                    <div
                      data-testid={`hiw-step-desc-${i}`}
                      className="text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none prose-headings:mt-2 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 break-words [overflow-wrap:anywhere] prose-a:break-all"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.description) }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {(settings.faq || []).length > 0 && (
        <section className="max-w-3xl mx-auto px-5 py-16">
          <h2 className="font-display text-3xl font-semibold text-center text-slate-900 mb-10">Domande frequenti</h2>
          <div className="space-y-3">
            {settings.faq.map((q, i) => (
              <div key={i} data-testid={`faq-item-${i}`} className="border border-slate-200 rounded-lg bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50"
                >
                  <span className="font-medium text-slate-800">{q.question}</span>
                  {openFAQ === i ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>
                {openFAQ === i && (
                  <div className="px-5 pb-4 pt-1 text-sm text-slate-600 leading-relaxed whitespace-pre-line border-t border-slate-100">
                    {q.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300">
        <div className="max-w-6xl mx-auto px-5 py-10 text-center">
          {settings.footer_text ? (
            <p className="whitespace-pre-line text-sm leading-relaxed">{settings.footer_text}</p>
          ) : (
            <p className="text-sm">© {new Date().getFullYear()} GestAgePro</p>
          )}
        </div>
      </footer>

      {/* 🆕 Feb 2026 — Dialog Richiedi una demo */}
      <Dialog open={demoOpen} onOpenChange={(v) => {
        if (!v) closeDemoDialog();
        else { setDemoOpen(true); setDemoOpenedAt(Date.now()); }
      }}>
        <DialogContent className="max-w-md" data-testid="demo-request-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-600" />
              {demoSubmitted ? "Richiesta ricevuta" : "Richiedi una demo"}
            </DialogTitle>
            {!demoSubmitted && (
              <DialogDescription>
                Compila i tuoi dati: un nostro consulente ti contatterà per attivare la demo gratuita.
              </DialogDescription>
            )}
          </DialogHeader>
          {demoSubmitted ? (
            <>
              <div
                data-testid="demo-request-confirmation"
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-900 whitespace-pre-line leading-relaxed"
              >
                <CheckCircle2 className="inline h-5 w-5 mr-1 text-emerald-600 align-text-bottom" />
                {settings.demo_request_confirmation_message
                  || "Grazie! La tua richiesta è stata inviata. Un nostro consulente ti contatterà a breve."}
              </div>
              <DialogFooter>
                <Button
                  data-testid="demo-close-btn"
                  onClick={closeDemoDialog}
                  className="bg-[#0A192F] hover:bg-[#1E293B]"
                >
                  Chiudi
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="demo-name">Nome *</Label>
                    <Input id="demo-name" data-testid="demo-input-name" value={demoForm.name}
                      onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="demo-surname">Cognome *</Label>
                    <Input id="demo-surname" data-testid="demo-input-surname" value={demoForm.surname}
                      onChange={(e) => setDemoForm({ ...demoForm, surname: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="demo-email">Email *</Label>
                  <Input id="demo-email" type="email" data-testid="demo-input-email" value={demoForm.email}
                    onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="demo-phone">Telefono *</Label>
                  <Input id="demo-phone" type="tel" data-testid="demo-input-phone" value={demoForm.phone}
                    onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                    placeholder="+39 333 1234567" />
                </div>
                <div>
                  <Label htmlFor="demo-agency">Nome agenzia *</Label>
                  <Input id="demo-agency" data-testid="demo-input-agency" value={demoForm.agency_name}
                    onChange={(e) => setDemoForm({ ...demoForm, agency_name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="demo-vat">Partita IVA *</Label>
                    <Input id="demo-vat" data-testid="demo-input-vat" value={demoForm.vat_number}
                      onChange={(e) => setDemoForm({ ...demoForm, vat_number: e.target.value })}
                      placeholder="11 cifre" maxLength={13} />
                  </div>
                  <div>
                    <Label htmlFor="demo-cf">Codice Fiscale *</Label>
                    <Input id="demo-cf" data-testid="demo-input-cf" value={demoForm.fiscal_code}
                      onChange={(e) => setDemoForm({ ...demoForm, fiscal_code: e.target.value.toUpperCase() })}
                      placeholder="16 caratteri" maxLength={16} />
                  </div>
                </div>
                {/* 🆕 Feb 2026 — Honeypot anti-bot: invisibile agli umani, i bot lo compilano e vengono droppati */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label htmlFor="demo-website">Sito web (lascia vuoto)</label>
                  <input
                    type="text"
                    id="demo-website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={demoForm.website}
                    onChange={(e) => setDemoForm({ ...demoForm, website: e.target.value })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={closeDemoDialog} disabled={demoSubmitting}>Annulla</Button>
                <Button
                  data-testid="demo-submit-btn"
                  onClick={submitDemoRequest}
                  disabled={demoSubmitting}
                  className="bg-[#0A192F] hover:bg-[#1E293B]"
                >
                  {demoSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Invia richiesta
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
